import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/header.scss";

const navItems = [
  { label: "New In", to: "/shop?sort=newest" },
  { label: "Shop", to: "/shop", hasMenu: true },
  { label: "Info", to: "/about" },
  { label: "Core Cuts", to: "/shop?category=Bottoms" },
  { label: "The Outlet", to: "/shop?sort=price-asc" },
  { label: "Contact", to: "/contact" },
];

const shopGroups = [
  {
    title: "Bottoms",
    links: ["Slim Cut", "Straight Cut", "Slim Tapered", "Relaxed Tapered"],
  },
  {
    title: "Jackets",
    links: ["Denim Jackets", "Leather Jackets", "Military Jackets", "Chore Jackets"],
  },
  {
    title: "Tops",
    links: ["Shirts", "T-Shirts", "Knits", "Work Shirts"],
  },
  {
    title: "Accessories",
    links: ["Bags", "Belts", "Bandanas", "Footwear"],
  },
];

const getCategoryForLink = (groupTitle, label) => {
  if (["T-Shirts", "Knits", "Footwear"].includes(label)) return label;
  if (groupTitle === "Tops") return "Shirts";
  return groupTitle;
};

const searchTermByLink = {
  "Slim Cut": "slim cut",
  "Straight Cut": "straight cut",
  "Slim Tapered": "slim tapered",
  "Relaxed Tapered": "relaxed tapered",
  "Denim Jackets": "denim jacket",
  "Leather Jackets": "leather jacket",
  "Military Jackets": "military jacket",
  "Chore Jackets": "work jacket",
  "Work Shirts": "work shirt",
  Bags: "bag",
  Belts: "belt",
  Bandanas: "bandana",
};

const getShopTarget = (groupTitle, label) => {
  const category = getCategoryForLink(groupTitle, label);
  const searchTerm = searchTermByLink[label];
  return `/shop?category=${encodeURIComponent(category)}${
    searchTerm ? `&q=${encodeURIComponent(searchTerm)}` : ""
  }`;
};

function Header() {
  const { cartCount, cartItems, wishlist, removeFromCart } = useContext(CartContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setCartOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.classList.toggle("menu-open", menuOpen || cartOpen);
    return () => document.body.classList.remove("menu-open");
  }, [cartOpen, menuOpen]);

  useEffect(() => {
    if (!menuOpen && !searchOpen && !cartOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setSearchOpen(false);
        setCartOpen(false);
      }
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [cartOpen, menuOpen, searchOpen]);

  const cartLines = cartItems
    .map((item) => ({
      ...item,
      product: productData.find((product) => product.id === item.id),
    }))
    .filter((item) => item.product);
  const cartSubtotal = cartLines.reduce(
    (sum, item) => sum + item.product.price * item.quantity,
    0
  );

  const submitSearch = (event) => {
    event.preventDefault();
    const value = query.trim();
    navigate(value ? `/shop?q=${encodeURIComponent(value)}` : "/shop");
  };

  const topAndClose = () => {
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="site-header-shell">
      <div className="promo-bar" aria-label="Store announcements">
        <Link to="/shipping">
          Free worldwide* shipping over {formatPrice(siteConfig.shippingThreshold)}
        </Link>
        <Link to="/shop?sort=newest">Fall / Winter 2026 preview now live</Link>
        <span className="promo-bar__country">United Kingdom · GBP</span>
      </div>

      <header className="site-header">
        <div className="site-header__row">
          <div className="site-header__mobile-actions">
            <button
              type="button"
              className="icon-button"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={() => {
                setCartOpen(false);
                setMenuOpen((open) => !open);
              }}
            >
              {menuOpen ? <FiX /> : <FiMenu />}
            </button>
            <button
              type="button"
              className="icon-button icon-button--mobile-search"
              aria-label="Open search"
              aria-expanded={searchOpen}
              onClick={() => {
                setCartOpen(false);
                setSearchOpen((open) => !open);
              }}
            >
              <FiSearch />
            </button>
          </div>

          <Link className="wordmark" to="/" onClick={topAndClose} aria-label={`${siteConfig.brandName} home`}>
            <span>{siteConfig.wordmark}</span>
            <small>Genuine quality · Made in Japan</small>
          </Link>

          <nav className="site-header__nav" aria-label="Primary navigation">
            <ul>
              {navItems.map((item) => (
                <li key={item.label} className={item.hasMenu ? "nav-item--shop" : ""}>
                  <Link to={item.to} onClick={topAndClose}>
                    {item.label}
                  </Link>
                  {item.hasMenu && (
                    <div className="mega-menu">
                      <div className="mega-menu__inner">
                        {shopGroups.map((group) => (
                          <section key={group.title}>
                            <h2>{group.title}</h2>
                            {group.links.map((label) => (
                              <Link
                                key={label}
                                to={getShopTarget(group.title, label)}
                                onClick={topAndClose}
                              >
                                {label}
                              </Link>
                            ))}
                          </section>
                        ))}
                        <aside>
                          <span className="eyebrow">The essentials</span>
                          <h2>Built heavy.<br />Worn hard.</h2>
                          <Link className="button-light" to="/shop" onClick={topAndClose}>
                            View all products
                          </Link>
                        </aside>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="site-header__actions">
            <button
              type="button"
              className="header-search-toggle"
              onClick={() => {
                setCartOpen(false);
                setSearchOpen((open) => !open);
              }}
              aria-expanded={searchOpen}
            >
              <span>Search</span>
              <FiSearch />
            </button>
            <Link className="icon-button icon-button--account" to="/contact" aria-label="Customer service">
              <FiUser />
            </Link>
            <Link className="icon-button icon-button--wishlist" to="/shop?wishlist=1" aria-label={`Wishlist, ${wishlist.length} items`}>
              <FiHeart />
              {wishlist.length > 0 && <span className="icon-badge">{wishlist.length}</span>}
            </Link>
            <button
              className="icon-button icon-button--cart"
              type="button"
              aria-label={`Shopping bag, ${cartCount} items`}
              aria-expanded={cartOpen}
              onClick={() => {
                setMenuOpen(false);
                setSearchOpen(false);
                setCartOpen(true);
              }}
            >
              <FiShoppingBag />
              {cartCount > 0 && <span className="icon-badge">{cartCount}</span>}
            </button>
          </div>
        </div>

        <div className={`header-search-panel ${searchOpen ? "is-open" : ""}`}>
          <form onSubmit={submitSearch} role="search">
            <FiSearch aria-hidden="true" />
            <label className="sr-only" htmlFor="site-search">Search products</label>
            <input
              id="site-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by product, fabric or style"
            />
            <button type="submit">Search</button>
          </form>
        </div>

        <div className={`mobile-drawer ${menuOpen ? "is-open" : ""}`} aria-hidden={!menuOpen}>
          <nav aria-label="Mobile navigation">
            {navItems.map((item) => (
              <Link key={item.label} to={item.to} onClick={topAndClose}>
                {item.label}<span aria-hidden="true">→</span>
              </Link>
            ))}
            <Link to="/shop?wishlist=1" onClick={topAndClose}>
              Wishlist ({wishlist.length})<span aria-hidden="true">→</span>
            </Link>
          </nav>
          <div className="mobile-drawer__categories">
            {shopGroups.map((group) => (
              <Link
                key={group.title}
                to={getShopTarget(group.title, group.title)}
                onClick={topAndClose}
              >
                {group.title}
              </Link>
            ))}
          </div>
          <p>{siteConfig.tagline}</p>
        </div>

        <div
          className={`cart-drawer-layer ${cartOpen ? "is-open" : ""}`}
          aria-hidden={!cartOpen}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setCartOpen(false);
          }}
        >
          <aside
            className="cart-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cart-drawer-title"
          >
            <header className="cart-drawer__header">
              <div>
                <span id="cart-drawer-title">Your bag</span>
                <small>{cartCount} {cartCount === 1 ? "item" : "items"}</small>
              </div>
              <strong>{formatPrice(cartSubtotal)}</strong>
              <button type="button" onClick={() => setCartOpen(false)} aria-label="Close shopping bag">
                <FiX />
              </button>
            </header>

            {cartLines.length > 0 ? (
              <div className="cart-drawer__items">
                {cartLines.map((item) => (
                  <article key={`${item.id}-${item.size}-${item.color}`}>
                    <Link to={`/shopDetails/${item.product.id}`}>
                      <img src={item.product.image} alt={item.product.name} />
                    </Link>
                    <div>
                      <Link to={`/shopDetails/${item.product.id}`}>{item.product.name}</Link>
                      <span>{item.color} · {item.size} · Qty {item.quantity}</span>
                      <strong>{formatPrice(item.product.price * item.quantity)}</strong>
                      <button
                        type="button"
                        onClick={() => removeFromCart(item.id, item.size, item.color)}
                      >
                        Remove
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="cart-drawer__empty">
                <FiShoppingBag aria-hidden="true" />
                <h2>You haven't added any items yet</h2>
                <p>Explore heavyweight denim and enduring layers.</p>
                <Link className="button-primary" to="/shop">Start shopping</Link>
              </div>
            )}

            {cartLines.length > 0 && (
              <footer className="cart-drawer__footer">
                <div><span>Subtotal</span><strong>{formatPrice(cartSubtotal)}</strong></div>
                <p>Taxes included. Shipping calculated at checkout.</p>
                <Link className="button-secondary" to="/cartpage">Review your cart</Link>
                <Link className="button-primary" to="/checkout">Checkout now</Link>
              </footer>
            )}
          </aside>
        </div>
      </header>
    </div>
  );
}

export default Header;
